import React, { memo } from 'react';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsPremiumJSW } from '@atlassian/jira-atlassian-navigation/src/controllers/edition/index.tsx';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

const JiraSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const isPremium = useIsPremiumJSW();
	const customOnboardinLozengeMessage = !isPremium ? messages.premiumLabel : messages.newLabel;
	const shouldShowLozenge = !isPremium || fg('onboarding-hub-new-lozenge');

	return (
		<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
			<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'jiraConfiguration' }}
					isSelected={currentRoute.name === 'products-jsw-configuration'}
					href="/jira/settings/products/jira-software-configuration"
				>
					{formatMessage(messages.configurationItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'storeManagement' }}
					isSelected={currentRoute.name === 'products-jsw-storage-management'}
					href="/jira/settings/products/jira-software-configuration/storage-management"
				>
					{formatMessage(messages.storeManagementItem)}
				</LinkMenuItem>
				{fg('jira-guest-access') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'guestAccess' }}
						isSelected={currentRoute.name === 'products-jsw-guest-access'}
						href="/jira/settings/products/jira-software-configuration/guest-access"
					>
						{formatMessage(messages.guestAccessItem)}
					</LinkMenuItem>
				)}
				{/* 
				This gate is setup to be enabled till Feb 28th 2026 (3 months from when the feature goes live to RT customers in Nov 2025). 
				Please refrain from cleaning this up till then
				 */}
				{fg('jira-onboarding-hub-gate') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'onboardingHub' }}
						isSelected={[
							'products-onboarding-hub',
							'products-onboarding-hub-create',
							'products-onboarding-hub-edit',
						].includes(currentRoute.name)}
						href="/jira/settings/products/onboarding"
						elemAfter={
							shouldShowLozenge ? (
								<Lozenge appearance="new">{formatMessage(customOnboardinLozengeMessage)}</Lozenge>
							) : null
						}
					>
						{formatMessage(messages.onboardingHubItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

export default memo(JiraSection);
